/* istanbul ignore file */
import React from 'react';
import { INVOICE_STATUSES_OPTIONS, INVOICE_STATUSES } from 'domain/finance/invoice/useInvoiceService';
import CreateInvoiceButton from 'domain/finance/invoice/shared/button/CreateInvoiceButton';
import InvoiceLink from 'domain/finance/invoice/shared/field/InvoiceLink';
import ViewInvoiceButton from 'domain/finance/invoice/shared/button/ViewInvoiceButton';
import { EnumField, TextField, BadgeField, DateField } from 'framework/ui/field';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import InvoiceStatusFilter from 'domain/finance/invoice/shared/filter/InvoiceStatusFilter';
import MutableInvoiceBadge from 'domain/finance/invoice/shared/MutableInvoiceBadge';
import ListPageHeaderWrapper from 'domain/finance/shared/ListPageHeaderWrapper';
import { OfficeCodeFilter } from 'domain/shared/employee';
import invoiceFilterValidation from 'domain/finance/invoice/List/invoiceFilterValidation';
import { LedgerLink } from 'domain/shared/ledger';
import RunInvoiceSubTotalUpdateButton from 'domain/finance/invoice/shared/button/RunInvoiceSubTotalUpdateButton';
import DownloadInvoicesReportButton from 'domain/finance/invoice/shared/button/DownloadInvoicesReportButton';
import TempInvoiceVendorNameField from '../shared/field/TempInvoiceVendorNameField';
import {
  Filters,
  QuickFilter,
  BooleanFilter,
  DateRangeFilter,
  FiltersLayout,
  ListHeader,
  DataGridLayout,
  ListPageLayout,
} from 'framework/list';

const InvoiceListPageContent = ({
  isSensitiveDataIsShown,
  toggleSensitiveDataButton,
}: {
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataButton?: Nullable<React.ReactElement>;
}) => {
  const filters = (
    <Filters getValidationSchema={() => Promise.resolve(invoiceFilterValidation)}>
      <FiltersLayout
        quickFilters={[
          <QuickFilter label="All pending invoices" state={{ status: { in: [INVOICE_STATUSES.PENDING] } }} />,
          <QuickFilter label="All approved invoices" state={{ status: { in: [INVOICE_STATUSES.APPROVED] } }} />,
        ]}
      >
        <InvoiceStatusFilter label="Status" source="status" />
        <DateRangeFilter source="invoiceDate" label="Period" />
        <OfficeCodeFilter source="office" label="Office" />
        <BooleanFilter source="isFutureExpense" label="Future expense" />
        <BooleanFilter source="isInvoiceDocumentWillArriveLater" label="Can be updated" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <ListPageLayout
      header={
        <ListPageHeaderWrapper
          header={<ListHeader label="Invoices" />}
          actions={[toggleSensitiveDataButton, <DownloadInvoicesReportButton />, <RunInvoiceSubTotalUpdateButton />]}
        />
      }
      actions={[<CreateInvoiceButton />]}
      filter={filters}
      // TODO: uncomment after fixing search on BE
      // search={<SearchInput label="Name" />}
    >
      <DataGridLayout buttons={[<ViewInvoiceButton source="id" />]}>
        {
          [
            <BadgeField source="id" label="Invoice number" badges={[<MutableInvoiceBadge />]} width="17%">
              <InvoiceLink source="id" nameSource="invoiceNumber" label="Invoice number" />
            </BadgeField>,
            <TempInvoiceVendorNameField
              nameSource="name"
              vendorSource="vendorId"
              vendorNameSource="vendorName"
              label="Vendor name"
              width="27%"
            />,
            isSensitiveDataIsShown ? (
              <InvoiceTotalField label="Total" source="subTotal.amount" currencySource="currency" width="10%" />
            ) : null,
            <EnumField label="Status" source="status" options={INVOICE_STATUSES_OPTIONS} width="10%" isSortable />,
            <DateField source="invoiceDate" label="Date" width="10%" isSortable />,
            <LedgerLink source="account" nameSource="account" label="Account" width="22%" />,
            <TextField source="description" width="25%" />,
          ].filter(Boolean) as React.ReactElement[]
        }
      </DataGridLayout>
    </ListPageLayout>
  );
};

export default InvoiceListPageContent;
